import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const useVoucherFunctions = ({
  dataSource,
  setDataSource,
  path,
  fieldName,
}) => {
  const navigate = useNavigate();
  const [total, setTotal] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  // const [totalDrAmount, setFnTotalDrAmount] = useState(0);
  // const [totalCrAmount, setFnTotalCrAmount] = useState(0);
  const [selectedItems, setSelectedItems] = useState([]);

  // Add new row
  const handleAdd = (rowTemplate) => {
    const newData = {
      key: dataSource?.length + Math.floor(Math.random() * 201),
      ...rowTemplate,
    };
    setDataSource((prevData) => {
      const newDataSource = [...prevData, newData];
      setSelectedItems(
        newDataSource?.map((item) => item[fieldName]).filter(Boolean),
      );
      recalculateTotalAmount(newDataSource);
      return newDataSource;
    });
  };

  const handleJournalAdd = (rowTemplate) => {
    const newData = {
      key: dataSource?.length + Math.floor(Math.random() * 201),
      ...rowTemplate,
    };

    setDataSource((prevData) => {
      const totalDrAmount = prevData.reduce(
        (sum, row) => sum + (row.dramount || 0),
        0,
      );
      const totalCrAmount = prevData.reduce(
        (sum, row) => sum + (row.cramount || 0),
        0,
      );
      const newDataSource = [...prevData, newData];

      // Get the new row just added
      const lastIndex = newDataSource.length - 1;
      const prevRow = prevData[prevData.length - 1];

      if (prevData.length > 0 && prevRow) {
        // Logic to balance the new entry
        const drExcess = totalDrAmount - totalCrAmount;
        const crExcess = totalCrAmount - totalDrAmount;

        if (drExcess > 0) {
          // Dr > Cr: Add Cr to new row to balance
          newDataSource[lastIndex] = {
            ...newDataSource[lastIndex],
            cramount: drExcess,
            dramount: 0,
          };
        } else if (crExcess > 0) {
          // Cr > Dr: Add Dr to new row to balance
          newDataSource[lastIndex] = {
            ...newDataSource[lastIndex],
            dramount: crExcess,
            cramount: 0,
          };
        } else {
          // Already balanced: add zero entry
          newDataSource[lastIndex] = {
            ...newDataSource[lastIndex],
            dramount: 0,
            cramount: 0,
          };
        }
      }

      // Update selected items if required
      setSelectedItems(
        newDataSource?.map((item) => item[fieldName]).filter(Boolean),
      );

      return newDataSource;
    });
  };

  const handleSave = (row) => {
    const newData = dataSource?.map((item) =>
      item?.key === row?.key ? { ...item, ...row } : item,
    );
    setDataSource(newData);
    setSelectedItems(newData?.map((item) => item[fieldName]).filter(Boolean));
    recalculateTotal(
      newData?.map((item) => ({
        ...item,
        dramount: item?.amount,
      })),
    );
    recalculateTotalAmount(newData);
  };

  const handleDrCrAmount = (id, field, value) => {
    const numValue = value === '' ? 0 : Number(value);

    // Find the current entry's index
    const currentIndex = dataSource.findIndex((entry) => entry.key === id);
    if (currentIndex === -1) return;

    // Create a new entries array
    const updatedEntries = [...dataSource];

    // Update the current entry with the new value
    updatedEntries[currentIndex] = {
      ...updatedEntries[currentIndex],
      [field]: numValue,
    };

    // If the other field has a value, clear it
    // We don't want both Dr and Cr values in the same row
    if (field === 'dramount' && updatedEntries[currentIndex].cramount > 0) {
      updatedEntries[currentIndex].cramount = 0;
    } else if (
      field === 'cramount' &&
      updatedEntries[currentIndex].dramount > 0
    ) {
      updatedEntries[currentIndex].dramount = 0;
    }

    // If this is not the last row, update the next row
    if (currentIndex < updatedEntries.length - 1) {
      const nextEntry = updatedEntries[currentIndex + 1];

      if (field === 'dramount') {
        // If we changed Dr amount, update next row's Cr amount
        updatedEntries[currentIndex + 1] = {
          ...nextEntry,
          cramount: numValue,
          dramount: 0, // Clear Dr amount in next row
        };
      } else if (field === 'cramount') {
        // If we changed Cr amount, update next row's Dr amount
        updatedEntries[currentIndex + 1] = {
          ...nextEntry,
          dramount: numValue,
          cramount: 0, // Clear Cr amount in next row
        };
      }
    }

    // Update state
    setDataSource(updatedEntries);
    setSelectedItems(
      updatedEntries?.map((item) => item[fieldName]).filter(Boolean),
    );
    recalculateTotal(updatedEntries);
  };

  const handleCrAmount = (row) => {
    const currentIndex = dataSource.findIndex((item) => item.key === row.key);
    const newCrAmount = parseFloat(row.cramount) || 0;

    const updatedDataSource = dataSource.map((item, index) => {
      if (index === currentIndex) {
        // Update current row's cramount and clear dramount
        return { ...item, cramount: newCrAmount, dramount: 0 };
      } else if (index === currentIndex + 1) {
        // Update next row's dramount and clear cramount
        return { ...item, dramount: newCrAmount, cramount: 0 };
      }
      return item;
    });

    setDataSource(updatedDataSource);
    setSelectedItems(
      updatedDataSource?.map((item) => item[fieldName]).filter(Boolean),
    );
    recalculateTotal(updatedDataSource);
  };

  const handleDrAmount = (row) => {
    const currentIndex = dataSource.findIndex((item) => item.key === row.key);
    const newDrAmount = parseFloat(row.dramount) || 0;

    const updatedDataSource = dataSource.map((item, index) => {
      if (index === currentIndex) {
        // Update current row's dramount and clear cramount
        return { ...item, dramount: newDrAmount, cramount: 0 };
      } else if (index === currentIndex + 1) {
        // Update next row's cramount and clear dramount
        return { ...item, cramount: newDrAmount, dramount: 0 };
      }
      return item;
    });

    setDataSource(updatedDataSource);
    setSelectedItems(
      updatedDataSource?.map((item) => item[fieldName]).filter(Boolean),
    );
    recalculateTotal(updatedDataSource);
  };

  // Delete row
  const handleDelete = (key) => {
    if (dataSource.length === 1) {
      // If there's only one row, clear its data (don't delete it)
      const resetData = [
        {
          key: dataSource[0].key,
          fieldName: '',
          dramount: 0,
          cramount: 0,
          remarks: '',
        },
      ];
      const resetDataAmount = [
        {
          key: dataSource[0].key,
          fieldName: '',
          amount: 0,
          remarks: '',
        },
      ];
      setDataSource(resetData);
      setSelectedItems([]); // Reset selected items since the row is now empty
      recalculateTotal(resetData);
      recalculateTotalAmount(resetDataAmount);
    } else {
      // If there's more than one row, delete the selected row
      const newData = dataSource?.filter((item) => item.key !== key);

      // After deletion, rebalance the remaining rows
      balanceEntries(newData);

      setDataSource(newData);
      setSelectedItems(newData?.map((item) => item[fieldName]).filter(Boolean)); // Update selectedItems
      recalculateTotal(newData);
      recalculateTotalAmount(newData);
    }
  };

  // Helper function to balance all entries after changes
  const balanceEntries = (entries) => {
    if (entries.length <= 1) return entries;

    // For each row (except the last), ensure the next row has the complementary value
    for (let i = 0; i < entries.length - 1; i++) {
      const currentRow = entries[i];
      const nextRow = entries[i + 1];

      if (currentRow.dramount > 0 && currentRow.cramount === 0) {
        // Current row has Dr, next row should have Cr
        entries[i + 1] = {
          ...nextRow,
          cramount: currentRow.dramount,
          dramount: 0,
        };
      } else if (currentRow.cramount > 0 && currentRow.dramount === 0) {
        // Current row has Cr, next row should have Dr
        entries[i + 1] = {
          ...nextRow,
          dramount: currentRow.cramount,
          cramount: 0,
        };
      }
    }

    return entries;
  };

  // Recalculate the total amount for journal voucher
  const recalculateTotal = (data = []) => {
    const totalDr = data?.reduce(
      (acc, item) => acc + parseFloat(item.dramount || 0),
      0,
    );
    const totalCr = data?.reduce(
      (acc, item) => acc + parseFloat(item.cramount || 0),
      0,
    );

    const finalTotal = isNaN(totalDr) ? 0 : totalDr;
    setTotal(finalTotal);
  };

  const VoucherData = dataSource?.map((data, index) => ({
    sno: index + 1,
    ...data,
  }));

  // Recalculate the total amount for all voucher except journal voucher
  const recalculateTotalAmount = (data) => {
    const totalAmount = data?.reduce(
      (acc, item) => acc + (parseFloat(item?.amount) || 0),
      0,
    );
    setTotalAmount(totalAmount);
  };

  return {
    dataSource,
    VoucherData,
    total,
    totalAmount,
    handleAdd,
    handleJournalAdd,
    // setFnTotalDrAmount,
    // setFnTotalCrAmount,
    handleSave,
    handleCrAmount,
    handleDrAmount,
    handleDrCrAmount,
    handleDelete,
    selectedItems,
    setSelectedItems,
    recalculateTotal,
    recalculateTotalAmount,
    balanceEntries,
  };
};
